// These are the components for the JSA V2 hazards section.
import React, { useEffect } from "react";
// import { jsaV2HazardsUtils } from "../jsaV2Utils";
import styles from "./jsaV2HazardsComponents.module.css";

// Helper function to get field error for V2 hazards
const getHazardsV2FieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_hazards_v2.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

// This is the checkbox component for the hazards section.
export const JsaV2HazardsCheckbox = ({
  callbacks,
  label,
  attribute,
  otherCheckbox = false,
}) => {
  // Handling the change when the checkbox is checked
  const handleChange = (e) => {
    callbacks.v2Actions.updateJsaV2Hazards(
      callbacks.jsaData,
      attribute,
      e.target.checked,
      otherCheckbox,
      callbacks.emitPatch
    );
    // Commit immediately if provided
    try {
      const toSnake = (s) =>
        String(s).replace(/[A-Z]/g, (L) => `_${L.toLowerCase()}`);
      callbacks?.commit?.hazard?.(
        callbacks.jsaData,
        toSnake(attribute),
        e.target.checked
      );
    } catch {}
  };

  return (
    <div className={styles.jsaV2HazardsCheckbox}>
      <label>{label}</label>
      <input
        type="checkbox"
        checked={callbacks.jsaData.jsaHazardsV2[attribute]}
        onChange={handleChange}
        disabled={callbacks.jsaMode === "view"}
      />
    </div>
  );
};

export const JsaV2HazardsOtherDescribe = ({ callbacks }) => {
  // Handling the change when the other describe input is changed (local state only on onChange)
  const handleChange = (e) => {
    callbacks.v2Actions.updateJsaV2HazardsOtherDescribe(
      callbacks.jsaData,
      e.target.value,
      null // Don't emit on every keystroke
    );
  };

  // Handle blur - send patch to server
  const handleBlur = (e) => {
    callbacks.v2Actions.updateJsaV2HazardsOtherDescribe(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch // Emit on blur
    );
    try {
      callbacks?.commit?.hazard?.(
        callbacks.jsaData,
        "other_describe",
        callbacks.jsaData?.jsaHazardsV2?.otherDescribe || ""
      );
    } catch {}
  };

  // Get error for other_describe field
  const otherDescribeError = getHazardsV2FieldError(
    callbacks.autosaveErrors,
    "other_describe"
  );

  return (
    <div className={styles.jsaV2HazardsOtherRow}>
      <input
        type="text"
        className={`${styles.jsaV2HazardsOtherInput} ${otherDescribeError ? styles.inputError : ""}`}
        value={callbacks.jsaData.jsaHazardsV2.otherDescribe || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Describe other hazards"
        disabled={callbacks.jsaMode === "view"}
      />
      {otherDescribeError && (
        <span className={styles.errorText}>{otherDescribeError}</span>
      )}
    </div>
  );
};

// This is the actual component tab section for the V2 Hazards section.
export const JsaV2HazardsTab = ({ callbacks }) => {
  // Other Hazards Description - Show when "Other" checkbox is checked
  useEffect(() => {
    if (callbacks.jsaData.jsaHazardsV2.other) {
      if (!callbacks.jsaV2.hazards.showOtherHazDescribe) {
        callbacks.v2Actions.updateJsaV2Hazards(
          callbacks.jsaData,
          "other",
          true,
          true,
          callbacks.emitPatch
        );
      }
    } else {
      if (callbacks.jsaV2.hazards.showOtherHazDescribe) {
        callbacks.v2Actions.updateJsaV2Hazards(
          callbacks.jsaData,
          "other",
          false,
          false,
          callbacks.emitPatch
        );
      }
    }
  }, [
    callbacks.jsaData.jsaHazardsV2.other,
    callbacks.jsaV2.hazards.showOtherHazDescribe,
  ]);

  return (
    <div className={styles.jsaV2HazardsContainer}>
      <div className={styles.jsaV2HazardsInfoSection}>
        <p>
          Potential Health and Safety Hazards, including chemical, physical,
          safety, biological, and environmental (Check all that apply):
        </p>
      </div>
      <div className={styles.jsaV2HazardsCheckboxSection}>
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Aerial Lifts/Platforms"
          attribute="aerialLiftPlatform"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Chemical Burns/Contact"
          attribute="chemBurns"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Chemical Splash"
          attribute="chemSplash"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Confined Space Entry"
          attribute="cse"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Cut/Abrasions"
          attribute="cutAbrasion"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Demolition"
          attribute="demo"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Electrical"
          attribute="electrical"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Elevated Loads"
          attribute="elevatedLoad"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Ergonomics"
          attribute="ergonomics"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Explosion/Fire"
          attribute="explosionFire"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Eye Hazards/Flying Objects"
          attribute="eyeHazFlyingObj"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Heights/Fall > 6ft"
          attribute="fallMoreSixFt"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Heat/Cold Stress"
          attribute="heatColdStress"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Heavy Equipment"
          attribute="heavyEquipment"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Inhalation Hazard"
          attribute="inhalationHaz"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Manual Lifting"
          attribute="manualLift"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Noise"
          attribute="noise"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Overhead Electrical Hazards"
          attribute="overheadElecHaz"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Overexertion"
          attribute="overexertion"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Pinch Points"
          attribute="pinchPoint"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Poisonous Plants/Insects"
          attribute="poisonPlantInsect"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Pressure Lines/Equipment"
          attribute="pressureLineEquip"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Radiation"
          attribute="radiation"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Sharp Objects"
          attribute="sharpObj"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Slips, Trips, and Falls"
          attribute="slipTripFall"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Spills"
          attribute="spills"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Thermal Burns"
          attribute="thermalBurns"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Trenching, Exavations, Cave-Ins"
          attribute="trenchExcavCavein"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Water/Drowning Hazard"
          attribute="waterHaz"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Weather Conditions"
          attribute="weatherCond"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Welding/Cutting"
          attribute="weldCutting"
        />
        <JsaV2HazardsCheckbox
          callbacks={callbacks}
          label="Other"
          attribute="other"
          otherCheckbox={true}
        />
        {callbacks.jsaV2.hazards.showOtherHazDescribe && (
          <JsaV2HazardsOtherDescribe callbacks={callbacks} />
        )}
      </div>
    </div>
  );
};
